









































































































































































































































































































import Vue from "vue";
import { marked } from "marked";
import Discord from "@/Discord";
import Event from "@/classes/Event";
import User from "@/User";
import UserAvatar from "@/components/users/UserAvatar.vue";

export default Vue.extend({
  components: { UserAvatar },
  props: {
    event: {
      type: Object as () => Event,
      required: true,
    },
    eventHasFinished: {
      type: Boolean,
      required: true,
    },
    isAdmin: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      newRating: 0,
      newFeedback: "",
      medalRecipientId: null as string | null,
      medalComment: "",
    };
  },
  computed: {
    user() {
      return User.get();
    },
    userHasRated(): boolean {
      if (!this.user || !this.event.ratings) {
        return false;
      }
      return this.event.ratings.some(
        (rating) => rating.user.id === this.user!.id
      );
    },
    eventParticipants(): any[] {
      const participants: any[] = [];

      // Get all users from slot groups with their squad and role info
      if (this.event.slotGroups) {
        this.event.slotGroups.forEach((slotGroup: any) => {
          if (slotGroup.playerSlots) {
            slotGroup.playerSlots.forEach((slot: any) => {
              if (slot.discordUser) {
                participants.push({
                  ...slot.discordUser,
                  squad: slotGroup.name,
                  role: slot.name,
                });
              }
            });
          }
        });
      }

      // Add reserve users (they don't have a squad/role)
      if (this.event.reserve) {
        this.event.reserve.forEach((reserveUser: any) => {
          participants.push({
            ...reserveUser,
            squad: "Reserve",
            role: null,
          });
        });
      }

      // Remove duplicates by user ID and filter out current user
      const uniqueParticipants = participants.filter(
        (participant, index, self) =>
          index === self.findIndex((p) => p.id === participant.id) &&
          (!this.user || participant.id !== this.user.id)
      );

      return uniqueParticipants;
    },
    canUserRate(): boolean {
      if (!this.user || !this.eventHasFinished) {
        return false;
      }

      // Check if user participated (was in a slot or reserve)
      const participated =
        this.event.slotGroups?.some((slotGroup: any) =>
          slotGroup.playerSlots?.some(
            (slot: any) =>
              slot.discordUser && slot.discordUser.id === this.user!.id
          )
        ) ||
        this.event.reserve?.some(
          (reserveUser: any) => reserveUser.id === this.user!.id
        );

      return participated && !this.userHasRated;
    },
    medalNominations(): any {
      if (!this.event.ratings || this.event.ratings.length === 0) {
        return null;
      }

      // Count medal votes per user
      const voteCounts: any = {};
      const voteComments: any = {};

      this.event.ratings.forEach((rating: any) => {
        if (rating.medalVote && rating.medalVoteComment) {
          const userId = rating.medalVote.id;

          if (!voteCounts[userId]) {
            voteCounts[userId] = 0;
            voteComments[userId] = {
              user: rating.medalVote,
              comments: [],
            };
          }

          voteCounts[userId]++;
          voteComments[userId].comments.push({
            comment: rating.medalVoteComment,
            voter: rating.user,
          });
        }
      });

      // Find user with most votes
      let maxVotes = 0;
      let winnerId = null;

      Object.keys(voteCounts).forEach((userId) => {
        if (voteCounts[userId] > maxVotes) {
          maxVotes = voteCounts[userId];
          winnerId = userId;
        }
      });

      if (!winnerId || maxVotes === 0) {
        return null;
      }

      return {
        user: voteComments[winnerId].user,
        voteCount: maxVotes,
        comments: voteComments[winnerId].comments,
      };
    },
    visibleRatings(): any[] {
      if (!this.event.ratings) {
        return [];
      }
      // Moderators see all ratings, regular users only see non-hidden ratings
      if (this.isAdmin) {
        return this.event.ratings;
      }
      return this.event.ratings.filter((rating: any) => !rating.hidden);
    },
    ratingRules(): any[] {
      return [(v: any) => v > 0 || "Please select a rating"];
    },
    feedbackRules(): any[] {
      return [
        (v: any) => !!v || "Feedback is required",
        (v: any) =>
          (v && v.length >= 10) ||
          "Please provide at least 10 characters of feedback",
      ];
    },
    medalCommentRules(): any[] {
      if (!this.medalRecipientId) {
        return [];
      }
      return [
        (v: any) =>
          !!v || "Please explain why this player deserves recognition",
      ];
    },
    participantItemText(): (item: any) => string {
      return (item: any) => item.globalName || item.username;
    },
  },
  methods: {
    getRankIconUrl(filename: string): string {
      try {
        return require(`@/assets/images/icons/${filename}.svg`);
      } catch {
        return "";
      }
    },
    getAvatar(id: number, avatar: string) {
      return Discord.avatarSourceUri(id, avatar);
    },
    async submitRating() {
      const form: any = this.$refs.ratingForm;
      if (!form.validate()) {
        return;
      }

      try {
        const payload: any = {
          event_id: this.event.id,
          rating: this.newRating,
          feedback: this.newFeedback,
        };

        if (this.medalRecipientId && this.medalComment) {
          payload.medal_vote = this.medalRecipientId;
          payload.medal_vote_comment = this.medalComment;
        }

        await this.axios.post(`/arma/eventRatings`, payload);

        // Reset form
        this.newRating = 0;
        this.newFeedback = "";
        this.medalRecipientId = null;
        this.medalComment = "";

        // Reload event to show the new rating
        this.$emit("reload-event");
      } catch (error) {
        this.$store.dispatch("showErrorSnackbar", "Failed to submit rating.");
      }
    },
    async toggleRatingVisibility(rating: any) {
      try {
        await this.axios.patch(`/arma/eventRatings/${rating.id}`, {
          hidden: rating.hidden,
        });
      } catch (error) {
        this.$store.dispatch(
          "showErrorSnackbar",
          "Failed to update rating visibility."
        );
        // Revert the switch if the API call fails
        rating.hidden = !rating.hidden;
      }
    },
    getLikesCount(rating: any): number {
      return rating.likeCount !== undefined ? rating.likeCount : 0;
    },
    getUserLike(rating: any): any {
      if (!this.user || !rating.likes) {
        return null;
      }
      return rating.likes.find((like: any) => like.userId === this.user!.id);
    },
    renderMarkdown(text: string): string {
      if (!text) {
        return "";
      }
      return marked.parse(text, { async: false }) as string;
    },
    async toggleLike(rating: any) {
      if (!this.user || rating.user.id === this.user.id) {
        return;
      }

      try {
        const userLike = this.getUserLike(rating);

        if (userLike) {
          // Unlike: DELETE request
          await this.axios.delete(`/api/arma/eventRatingLikes/${userLike.id}`);
          // Remove the like from the array
          if (rating.likes) {
            rating.likes = rating.likes.filter(
              (like: any) => like.id !== userLike.id
            );
          }
          // Update like count
          if (rating.likeCount !== undefined) {
            rating.likeCount = Math.max(0, rating.likeCount - 1);
          }
        } else {
          // Like: POST request
          const response = await this.axios.post(`/api/arma/eventRatingLikes`, {
            rating_id: rating.id,
          });
          // Add the new like to the array
          if (!rating.likes) {
            rating.likes = [];
          }
          rating.likes.push(response.data);
          // Update like count
          if (rating.likeCount !== undefined) {
            rating.likeCount += 1;
          } else {
            rating.likeCount = 1;
          }
        }
      } catch (error) {
        this.$store.dispatch("showErrorSnackbar", "Failed to update like.");
      }
    },
    handleAvatarError(e: any) {
      const target = e.target as HTMLImageElement;
      // Set a placeholder avatar using a generic Discord default avatar
      target.src = "https://cdn.discordapp.com/embed/avatars/0.png";
    },
  },
});
